import { z } from 'zod';
import { ApiClient } from '../lib/api';
import { transformIncomingLineItemTypes, transformOutgoingLineItemTypes } from '../lib/transformations';
import {
  QuoteSection
} from '../models/line-item';
import {
  CreateQuote,
  CreateQuoteSchema,
  Quote,
  QuoteSchema,
  QuoteSectionsApiSchema,
  UpdateQuote,
  UpdateQuoteSchema
} from '../models/quote';
import { CreateQuoteCalculator, QuoteCalculator, QuoteCalculatorSchema, UpdateQuoteCalculator } from '@/models';

export class QuoteService {
  private api: ApiClient;

  constructor(baseURL: string, stage: string) {
    this.api = new ApiClient(`${baseURL}/${stage}/quote-service-${stage}`, stage);
  }

  private setDefaults(data: Partial<Quote>): Quote {
    return {
      ...data,
      jrtaxrate: data.jrtaxrate ?? 0,
      jrtaxamt: data.jrtaxamt ?? 0,
      jrtotalcost: data.jrtotalcost ?? 0,
      jrdepreq: data.jrdepreq ?? 0,
    } as Quote;
  }

  async getAllQuotes(): Promise<Quote[]> {
    const quotes = await this.api.request(
      { method: 'GET', url: '/quotes' },
      QuoteSchema.array()
    );
    return quotes.map(quote => this.setDefaults(transformIncomingLineItemTypes(quote)));
  }

  async getQuoteById(id: number): Promise<Quote> {
    const quote = await this.api.request(
      { method: 'GET', url: `/quotes/${id}` },
      QuoteSchema
    );
    return this.setDefaults(transformIncomingLineItemTypes(quote));
  }

  async createQuote(data: CreateQuote): Promise<Quote> {
    const quote = await this.api.request(
      {
        method: 'POST',
        url: '/quotes',
        data: transformOutgoingLineItemTypes(CreateQuoteSchema.parse(data))
      },
      QuoteSchema
    );
    return this.setDefaults(transformIncomingLineItemTypes(quote));
  }

  async updateQuote(id: number, data: UpdateQuote): Promise<Quote> {
    const quote = await this.api.request(
      {
        method: 'PUT',
        url: `/quotes/${id}`,
        data: transformOutgoingLineItemTypes(UpdateQuoteSchema.parse(data))
      },
      QuoteSchema
    );
    return this.setDefaults(transformIncomingLineItemTypes(quote));
  }

  async deleteQuote(id: number): Promise<void> {
    await this.api.request(
      { method: 'DELETE', url: `/quotes/${id}` },
      z.void()
    );
  }

  // New methods for quote sections
  async getQuoteSections(quoteId: number): Promise<QuoteSection[]> {
    try {
      const response = await this.api.request(
        { method: 'GET', url: `/quotes/${quoteId}/sections` },
        QuoteSectionsApiSchema
      );

      return response.map(section => ({
        id: section.jrsid,
        trackingId: section.jrstrackingid ?? crypto.randomUUID(),
        name: section.jrsname,
        order: section.jrsorder,
        wotype: section.jrswotype,
        crewId: section.jrscruid ?? null,
        isCollapsed: false
      }));
    } catch (error) {
      console.error("Error fetching quote sections:", error);
      return [];
    }
  }

  async updateQuoteSections(
    quoteId: number,
    sections: QuoteSection[]
  ): Promise<QuoteSection[]> {
    const apiSections = sections.map(section => ({
      jrsid: section.id,
      jrstrackingid: section.trackingId,
      jrsjrid: quoteId,
      jrscruid: section.crewId ?? null,
      jrsname: section.name,
      jrsorder: section.order,
      jrswotype: section.wotype
    }));

    const response = await this.api.request(
      {
        method: 'PUT',
        url: `/quotes/${quoteId}/sections`,
        data: apiSections,
      },
      QuoteSectionsApiSchema
    );

    return response.map(section => ({
      id: section.jrsid,
      trackingId: section.jrstrackingid ?? crypto.randomUUID(),
      name: section.jrsname,
      order: section.jrsorder,
      wotype: section.jrswotype,
      crewId: section.jrscruid ?? null,
      isCollapsed: false
    }));
  }

  async getQuoteCalculatorByJobId(jobId: number): Promise<QuoteCalculator | null> {
    return this.api.request(
      { method: 'GET', url: `/jobs/${jobId}/quote-calculator` },
      QuoteCalculatorSchema.nullable()
    );
  }

  async createQuoteCalculator(data: CreateQuoteCalculator): Promise<QuoteCalculator> {
    return this.api.request(
      { method: 'POST', url: `/quote-calculators`, data },
      QuoteCalculatorSchema
    );
  }

  async updateQuoteCalculator(id: number, data: UpdateQuoteCalculator): Promise<QuoteCalculator> {
    return this.api.request(
      { method: 'PUT', url: `/quote-calculators/${id}`, data },
      QuoteCalculatorSchema
    );
  }
} 